/**
 * useKeyboardShortcuts — per docs/specs/README.md
 * Global keyboard shortcut layer. Handles:
 *   - ⌘K / Ctrl+K  → toggle command palette
 *   - `g` + key    → chord-based navigation (g o, g u, g s, …)
 *   - `/`          → focus the topbar search
 *   - `?`          → open shortcut help overlay
 * Chords have a 1.2s window. Input/textarea/contenteditable targets are ignored
 * for alphabetic shortcuts so typing is never hijacked.
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-20-v2
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const CHORD_WINDOW_MS = 1200;

/** chord key → destination path. */
export const NAV_CHORDS: Record<string, string> = {
    o: '/',
    u: '/users',
    s: '/support',
    h: '/system-health',
    b: '/billing',
    m: '/affiliate-manager',
    a: '/analytics',
    e: '/error-logs'
};

/** Return true when the event target is a user-editable field. */
function isTypingTarget(el: EventTarget | null): boolean {
    if (!(el instanceof HTMLElement)) return false;
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (el.isContentEditable) return true;
    return false;
}

export interface KeyboardShortcutHandlers {
    /** Open or toggle the command palette. */
    onTogglePalette: () => void;
    /** Close palette if it's currently open (used for ⌘K when already open). */
    onClosePalette: () => void;
    /** Open the `?` shortcut help overlay. */
    onOpenHelp: () => void;
    /** Return `true` when the palette is currently mounted/open. */
    isPaletteOpen: () => boolean;
    /** Return `true` when the shortcut help overlay is currently open. */
    isHelpOpen: () => boolean;
}

/**
 * Attach global keyboard listeners to the document. Safe to unmount/remount.
 */
export function useKeyboardShortcuts(handlers: KeyboardShortcutHandlers) {
    const navigate = useNavigate();
    // Latest-handlers ref so the listener never sees stale closures.
    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;

    useEffect(() => {
        let chordPending = false;
        let chordTimer: number | null = null;

        const resetChord = () => {
            chordPending = false;
            if (chordTimer !== null) {
                window.clearTimeout(chordTimer);
                chordTimer = null;
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            // ⌘K / Ctrl+K — always active, even inside inputs.
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                if (handlersRef.current.isPaletteOpen()) {
                    handlersRef.current.onClosePalette();
                } else {
                    handlersRef.current.onTogglePalette();
                }
                resetChord();
                return;
            }

            // All other shortcuts are ignored while typing.
            if (isTypingTarget(e.target)) return;

            // Don't interfere with modifier-key combos we don't own.
            if (e.metaKey || e.ctrlKey || e.altKey) return;

            // If palette or help overlay is open, suppress chord nav.
            if (handlersRef.current.isPaletteOpen() || handlersRef.current.isHelpOpen()) {
                return;
            }

            const key = e.key;

            // `?` opens help.
            if (key === '?') {
                e.preventDefault();
                handlersRef.current.onOpenHelp();
                resetChord();
                return;
            }

            // `/` focuses the topbar search (which opens the palette).
            if (key === '/') {
                e.preventDefault();
                const search = document.querySelector<HTMLElement>('.topbar-search');
                if (search) search.click();
                resetChord();
                return;
            }

            // Chord starter: plain lower-case `g`.
            if (!chordPending && key === 'g') {
                e.preventDefault();
                chordPending = true;
                chordTimer = window.setTimeout(resetChord, CHORD_WINDOW_MS);
                return;
            }

            // Chord second key.
            if (chordPending) {
                const dest = NAV_CHORDS[key.toLowerCase()];
                resetChord();
                if (dest) {
                    e.preventDefault();
                    navigate(dest);
                }
                return;
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            resetChord();
        };
    }, [navigate]);
}
